"use client";

// Виджет «Перевод отделов на портал» (вкладка «Задачи»): по каждому отделу —
// прогресс перевода Google-таблиц на портал + раскрывающийся список КОНКРЕТНЫХ
// таблиц (какие и КУДА именно переехали: модуль+вкладка портала или текст/ссылка).
// Счётчики X/Y берутся из списка таблиц (если он есть), иначе — ручной фолбэк.

import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, ExternalLink } from "lucide-react";
import { IconCheck, IconChevronRight, IconLink, IconPlus, IconTrash } from "@/components/ui/icons";

import { api } from "@/lib/api";
import { Select } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ITPR_TABLE_STATUS, type ItprDeptMigration, type ItprDeptTable, type ItprDeptTableStatus } from "@/lib/it-tasks";
import { buildDeptTabTree, SYNTH_TAB_LABELS } from "@/lib/dept-tabs";
import type { PortalModule } from "@/lib/types";
import { INPUT_CLS } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const inputCls = INPUT_CLS;
// Поле названия таблицы в строке — без рамки, сливается со строкой (убираем «коробку в коробке»).
const nameInputCls = "min-w-0 flex-1 border-0 bg-transparent px-0 text-[12px] font-semibold text-[var(--text)] placeholder:text-[var(--textMuted)]/60 focus:outline-none";

// Вкладки отдела (по его модулям) — для выбора «куда переехала таблица».
function deptTabTree(deptKey: string, modules: PortalModule[]) {
  const deptModules = modules
    .filter((m) => (m.departmentKeys ?? []).includes(deptKey as never))
    .map((m) => ({ key: m.key, title: m.title }));
  return buildDeptTabTree(deptKey, deptModules);
}
// Плоский список опций Select: верхняя вкладка + дочерние «Вкладка › Подвкладка».
// value: topKey либо `topKey::childKey`.
function deptTabOptions(deptKey: string, modules: PortalModule[]): { value: string; label: string }[] {
  const opts = [{ value: "", label: "— не вкладка —" }];
  for (const top of deptTabTree(deptKey, modules)) {
    opts.push({ value: top.key, label: top.label });
    for (const c of top.children ?? []) opts.push({ value: `${top.key}::${c.key}`, label: `${top.label} › ${c.label}` });
  }
  return opts;
}
// Человекочитаемая подпись «куда» из сохранённых ключей (topKey + childKey).
//
// ⚠️ Ключи сохраняются НАВСЕГДА, а дерево вкладок отдела меняется: вкладку-модуль
// могут спрятать (в Маркетинге так убрали `creator_reports` — всё уехало в верхние
// вкладки), синтет-вкладку — скрыть (mppulse/10X в Продажах). Раньше в этом случае
// подпись вырождалась в сырые ключи («creator_reports › reports»), и в виджете
// висела невнятица. Теперь разбираем по слоям: дерево вкладок отдела → подписи
// синтет-вкладок (включая скрытые) → вкладки больше нет, значит и показывать нечего.
function deptTabLabel(deptKey: string, modules: PortalModule[], topKey: string, childKey: string): string {
  if (!topKey) return "";
  const top = deptTabTree(deptKey, modules).find((t) => t.key === topKey);
  if (top) {
    if (!childKey) return top.label;
    const c = (top.children ?? []).find((x) => x.key === childKey);
    // Подвкладки может уже не быть (её переименовали/убрали) — тогда показываем
    // только вкладку, а не «Вкладка › сырой_ключ».
    return c ? `${top.label} › ${c.label}` : top.label;
  }
  // Синтет-вкладка (в т.ч. скрытая) — у неё есть постоянная подпись.
  const synth = SYNTH_TAB_LABELS[topKey];
  if (synth) return synth;
  // Вкладки в дереве отдела больше нет (модуль убрали из отдела — так вышло с
  // «Отчётами креаторов» в Маркетинге: всё уехало в верхние вкладки). Такое место
  // назначения уже не существует, поэтому НЕ показываем его вовсе — пусть в строке
  // останутся только живые вкладки (у «Отчётности маркетинг» это «Креаторы»).
  return "";
}
// Подпись по value-строке ("top" | "top::child").
function tabValueLabel(deptKey: string, modules: PortalModule[], value: string): string {
  const [top, child] = value.split("::");
  return deptTabLabel(deptKey, modules, top, child ?? "");
}

export function ItprDeptMigrationWidget({ canEdit = false, компактно = false }: { canEdit?: boolean; компактно?: boolean }) {
  // ⚠️ КОМПАКТНЫЙ РЕЖИМ — для кабинета (Максим, 04.09.2026: «сейчас они огромные,
  // надо сделать компактные и уместить в этот ряд»). В ряду из пяти блоков на
  // карточку приходится ~300 px: убираем поясняющую строку, сужаем колонку
  // отдела и прячем плашку состояния — цвет полосы и так о нём говорит.
  const [depts, setDepts] = useState<ItprDeptMigration[]>([]);
  const [tables, setTables] = useState<ItprDeptTable[]>([]);
  const [modules, setModules] = useState<PortalModule[]>([]);
  const reqRef = useRef(0);

  const reload = async () => {
    const id = ++reqRef.current;
    try {
      const [dm, dt] = await Promise.all([api.itprListDeptMigration(), api.itprListDeptTables()]);
      if (id === reqRef.current) { setDepts(dm); setTables(dt); }
    } catch { /* тихо */ }
  };
  useEffect(() => { void reload(); api.getModules().then(setModules).catch(() => {}); }, []);

  const tablesByDept = useMemo(() => {
    const m = new Map<string, ItprDeptTable[]>();
    for (const t of tables) { const a = m.get(t.dept_key) ?? []; a.push(t); m.set(t.dept_key, a); }
    return m;
  }, [tables]);
  const totalTables = depts.reduce((s, d) => s + d.total_tables, 0);
  const migratedTables = depts.reduce((s, d) => s + d.migrated_tables, 0);
  const overallPct = totalTables > 0 ? Math.round((migratedTables / totalTables) * 100) : 0;

  // ── мутации списка таблиц ──
  function patchLocal(id: number, patch: Partial<ItprDeptTable>) {
    setTables((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }
  async function patchTable(id: number, patch: Partial<ItprDeptTable>, reloadCounts = false) {
    patchLocal(id, patch);
    try { await api.itprPatchDeptTable(id, patch); if (reloadCounts) await reload(); } catch { void reload(); }
  }
  async function addTable(deptKey: string) {
    try { await api.itprCreateDeptTable({ dept_key: deptKey, name: "" }); await reload(); } catch { /* тихо */ }
  }
  async function delTable(id: number) {
    setTables((prev) => prev.filter((t) => t.id !== id));
    try { await api.itprDeleteDeptTable(id); await reload(); } catch { void reload(); }
  }

  return (
    <div className="glass-panel flex h-full min-h-0 flex-col rounded-[var(--radius-lg)] border border-[var(--border)] p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="grid shrink-0 place-items-center rounded-[var(--radius-md)]" style={{ width: 30, height: 30, background: "color-mix(in srgb, #10b981 16%, var(--card))", color: "#10b981" }}>
          <Building2 size={16} />
        </span>
        <span className="truncate text-[12px] font-bold text-[var(--text)]" >Перевод отделов на портал</span>
        <Badge color="#10b981" className="ml-auto tabular-nums">{overallPct}%</Badge>
      </div>
      {!компактно && (
        <div className="mb-3 -mt-1 text-[11px] text-[var(--textMuted)]">Сколько таблиц отдела переведено на портал и куда именно{canEdit ? " · нажмите отдел, чтобы добавить таблицы" : ""}.</div>
      )}

      <div className={компактно ? "space-y-1" : "space-y-2"}>
        {depts.map((d) => {
          const list = tablesByDept.get(d.dept_key) ?? [];
          const hasList = list.length > 0;
          const pct = d.total_tables > 0 ? Math.round((d.migrated_tables / d.total_tables) * 100) : 0;
          const st = d.total_tables === 0 ? { t: "не начат", c: "#64748b" }
            : d.migrated_tables >= d.total_tables ? { t: "готово", c: "#10b981" }
            : { t: "в процессе", c: "#f59e0b" };
          return (
            <Popover key={d.dept_key}>
              <PopoverTrigger asChild>
                <Button title="Показать таблицы отдела" className={`h-auto w-full justify-start text-left ${компактно ? "py-1" : "py-2"}`} >
                  <IconChevronRight size={14} className="shrink-0 text-[var(--textMuted)]" />
                  <span className={`shrink-0 truncate font-semibold ${компактно ? "w-[74px] text-[11px]" : "w-32 text-[12px]"}`} data-tooltip={d.title}>{d.title}</span>
                  <span className="min-w-0 flex-1">
                    <span className="track block w-full">
                      <span className="block h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, color-mix(in srgb, ${st.c} 65%, transparent), ${st.c})` }} />
                    </span>
                  </span>
                  <span className="shrink-0 text-[12px] font-semibold tabular-nums">{d.migrated_tables}<span className="text-[var(--textMuted)]">/{d.total_tables}</span></span>
                  {!компактно && (
                    <>
                      <span className="w-10 shrink-0 text-right text-[11px] font-semibold tabular-nums" style={{ color: st.c }}>{pct}%</span>
                      <span className="w-[84px] shrink-0 rounded-[var(--radius-md)] px-2 py-0.5 text-center text-[11px] font-semibold"
                        style={{ color: st.c, background: `color-mix(in srgb, ${st.c} 14%, transparent)` }}>{st.t}</span>
                    </>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" sideOffset={6} className="w-[720px] max-w-[94vw] p-3"
                onOpenAutoFocus={(e) => e.preventDefault()}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-[12px] font-bold" >{d.title}</span>
                  <span className="text-[11px] text-[var(--textMuted)]">{d.migrated_tables}/{d.total_tables} переведено</span>
                </div>
                {!hasList && <div className="mb-1.5 text-[11px] text-[var(--textMuted)]">Таблицы не перечислены{canEdit ? " — добавьте первую" : ""}.</div>}
                <div className="max-h-[50dvh] space-y-1.5 overflow-y-auto scrollbar-autohide">
                  {list.map((t) => (
                    <TableRow key={t.id} t={t} canEdit={canEdit} modules={modules}
                      onPatch={patchTable} onDelete={delTable} />
                  ))}
                </div>
                {canEdit && (
                  <Button className="mt-2" onClick={() => void addTable(d.dept_key)}>
                    <IconPlus size={12} /> таблица
                  </Button>
                )}
              </PopoverContent>
            </Popover>
          );
        })}
        {depts.length === 0 && <div className="py-6 text-center text-[12px] text-[var(--textMuted)]">Нет отделов</div>}
      </div>
    </div>
  );
}

// Одна таблица отдела: название · статус · «куда» · исходник · удалить.
function TableRow({ t, canEdit, modules, onPatch, onDelete }: {
  t: ItprDeptTable; canEdit: boolean; modules: PortalModule[];
  onPatch: (id: number, patch: Partial<ItprDeptTable>, reloadCounts?: boolean) => void;
  onDelete: (id: number) => void;
}) {
  // Подпись «куда»: вкладки отдела (может быть несколько) + свободный текст/ссылка.
  // Дедуп: несколько подвкладок одного скрытого модуля сворачиваются в одну и ту же
  // подпись (три «creator_reports::*» → одни «Отчёты креаторов») — не дублируем.
  const tabLabels = [...new Set((t.target_tabs ?? []).map((v) => tabValueLabel(t.dept_key, modules, v)).filter(Boolean))];
  const freeLabel = t.target_label || (t.target_url ? "ссылка" : "");
  const targetLabel = [...tabLabels, ...(freeLabel ? [freeLabel] : [])].join(" · ");
  const stColor = ITPR_TABLE_STATUS[t.status].color;

  return (
    <div className="group relative flex items-center gap-2 overflow-hidden rounded-[var(--radius-md)] border border-[var(--border)] bg-[color-mix(in_srgb,var(--card)_55%,var(--bg))] py-1.5 pl-3.5 pr-2 transition hover:border-[color-mix(in_srgb,var(--primary)_50%,var(--border))]">
      {/* Цветная полоса статуса слева */}
      <span className="absolute inset-y-1 left-0 w-[3px] rounded-full" style={{ background: stColor }} />
      {/* Название */}
      {canEdit ? (
        <input className={nameInputCls} defaultValue={t.name} placeholder="Название таблицы…"
          onBlur={(e) => { if (e.target.value !== t.name) onPatch(t.id, { name: e.target.value }); }} />
      ) : <span className="min-w-0 flex-1 truncate text-[12px] font-semibold">{t.name || "—"}</span>}

      {/* Исходник (Google Sheet) */}
      {t.source_url
        ? <a href={t.source_url} target="_blank" rel="noreferrer" data-tooltip="Исходная таблица" className="shrink-0 text-[var(--textMuted)] hover:text-[var(--primary)]"><IconLink size={13} /></a>
        : canEdit && <SourceEditor t={t} onPatch={onPatch} />}

      {/* Статус */}
      {canEdit ? (
        <Select value={t.status} onChange={(v) => onPatch(t.id, { status: v as ItprDeptTableStatus }, true)}
          size="xs" className="w-[120px] shrink-0"
          options={(Object.keys(ITPR_TABLE_STATUS) as ItprDeptTableStatus[]).map((s) => ({ value: s, label: ITPR_TABLE_STATUS[s].label }))} />
      ) : (
        <span className="shrink-0 rounded-[var(--radius-md)] px-2 py-0.5 text-[11px] font-semibold"
          style={{ color: ITPR_TABLE_STATUS[t.status].color, background: `color-mix(in srgb, ${ITPR_TABLE_STATUS[t.status].color} 14%, transparent)` }}>
          {ITPR_TABLE_STATUS[t.status].label}
        </span>
      )}

      {/* Куда именно */}
      {canEdit ? (
        <TargetEditor t={t} modules={modules} label={targetLabel} onPatch={onPatch} />
      ) : targetLabel ? (
        t.target_url && tabLabels.length === 0
          ? <a href={t.target_url} target="_blank" rel="noreferrer" className="shrink-0 inline-flex max-w-[300px] items-center gap-1 truncate rounded-[var(--radius-md)] bg-[var(--secondary)] px-2 py-0.5 text-[11px] text-[var(--text)]"><ExternalLink size={10} /><span className="truncate">{targetLabel}</span></a>
          : <span className="shrink-0 max-w-[300px] truncate rounded-[var(--radius-md)] bg-[var(--secondary)] px-2 py-0.5 text-[11px] text-[var(--text)]" data-tooltip={targetLabel}>→ {targetLabel}</span>
      ) : <span className="shrink-0 text-[11px] text-[var(--textMuted)]/60">— куда —</span>}

      {/* Удалить */}
      {canEdit && (
        <button type="button" onClick={() => onDelete(t.id)} data-tooltip="Удалить таблицу"
          className="shrink-0 text-[var(--textMuted)]/60 hover:text-[#ef4444]"><IconTrash size={13} /></button>
      )}
    </div>
  );
}

// Редактор «куда»: НЕСКОЛЬКО вкладок отдела (чекбоксы) ИЛИ свободный текст/ссылка.
function TargetEditor({ t, modules, label, onPatch }: {
  t: ItprDeptTable; modules: PortalModule[]; label: string;
  onPatch: (id: number, patch: Partial<ItprDeptTable>) => void;
}) {
  const selected = t.target_tabs ?? [];
  const tabOpts = deptTabOptions(t.dept_key, modules).filter((o) => o.value !== "");
  const toggle = (value: string) => {
    const next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
    onPatch(t.id, { target_tabs: next });
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        {label ? (
          <button type="button" className="shrink-0 inline-flex max-w-[300px] items-center gap-1 truncate rounded-[var(--radius-md)] px-2 py-0.5 text-[11px] font-semibold transition hover:brightness-110"
            style={{ color: "#10b981", background: "color-mix(in srgb, #10b981 14%, transparent)" }}>
            <span className="truncate">→ {label}</span>
          </button>
        ) : (
          <button type="button" className="shrink-0 inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-dashed border-[var(--border)] px-2 py-0.5 text-[11px] text-[var(--textMuted)] transition hover:border-[var(--primary)] hover:text-[var(--primary)]">
            + куда
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="w-[380px] max-w-[92vw] space-y-2 p-3">
        <div className="field-label">Куда переехала · вкладки отдела</div>
        <div className="max-h-52 space-y-0.5 overflow-y-auto scrollbar-autohide">
          {tabOpts.map((o) => {
            const on = selected.includes(o.value);
            return (
              <button type="button" key={o.value} onClick={() => toggle(o.value)}
                className="glass-flat flex w-full items-center gap-2 rounded-[var(--radius-md)] px-1.5 py-1 text-left text-[12px] hover:bg-[var(--hover)]">
                <span className="grid h-4 w-4 shrink-0 place-items-center rounded-[5px] border transition"
                  style={{ borderColor: on ? "#10b981" : "var(--border)", background: on ? "#10b981" : "transparent" }}>
                  {on && <IconCheck size={11} className="text-white" />}
                </span>
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
              </button>
            );
          })}
          {tabOpts.length === 0 && <div className="px-1.5 py-1 text-[11px] text-[var(--textMuted)]">У отдела нет вкладок</div>}
        </div>
        <div className="text-center text-[11px] text-[var(--textMuted)]/70">или свободно</div>
        <input className={inputCls} defaultValue={t.target_label} placeholder="Описание (куда)"
          onBlur={(e) => { if (e.target.value !== t.target_label) onPatch(t.id, { target_label: e.target.value }); }} />
        <input className={inputCls} defaultValue={t.target_url} placeholder="Ссылка (https://…)"
          onBlur={(e) => { if (e.target.value !== t.target_url) onPatch(t.id, { target_url: e.target.value }); }} />
      </PopoverContent>
    </Popover>
  );
}

// Кнопка добавить ссылку на исходную Google-таблицу.
function SourceEditor({ t, onPatch }: { t: ItprDeptTable; onPatch: (id: number, patch: Partial<ItprDeptTable>) => void }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-tooltip="Ссылка на исходную таблицу" className="shrink-0 text-[var(--textMuted)]/60 hover:text-[var(--primary)]"><IconLink size={13} /></button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={4} className="w-[260px] p-2">
        <input className={inputCls} defaultValue={t.source_url} placeholder="Ссылка на Google Sheet"
          onBlur={(e) => { if (e.target.value !== t.source_url) onPatch(t.id, { source_url: e.target.value }); }} />
      </PopoverContent>
    </Popover>
  );
}
